<script>
import { preventDefault } from 'svelte/legacy';
import { onMount } from 'svelte';
import {
    authState
} from "@app/store/authStore.svelte.js";
import {
    modalState, hideModal, showModal
} from "@app/store/modalStore.svelte.js";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { markerState } from "@app/store/markerStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import { fade } from "svelte/transition";
import { BarLoader } from "svelte-loading-spinners";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import tcLib from "@cclib/lib/timecode.js";
/* Firebase */
import firebase from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';
import "firebase/functions";
/* Language Settings */
import deepgramLanguages from "@cclib/providers/deepgram/languages.js";
import assemblyAiLanguages from "@cclib/providers/assemblyAi/languages.js";
import speechmaticsLanguages from "@cclib/providers/speechmatics/languages.js";
import * as m from '@app/paraglide/messages.js';
/* Shared AI AD / SD config options */
import {
    OUTPUT_LANGUAGES,
    DESCRIPTION_LENGTH_PRESETS,
    DESCRIPTION_FOCUS_OPTIONS,
    DEFAULT_FOCUS,
    TRIAL_MAX_DURATION_SECONDS,
    MAX_WPM_PRESETS,
    clampSegment
} from "@app/lib/aiAd/options.js";
import { detectSceneChanges } from "@app/lib/detectSceneChanges.js";

/* FFmpeg */
const ffmpeg = window.ffmpeg;
const os = window.os;
const path = window.path;
const fs = window.fsSync;
const exec = window.exec;

let adDefaults = JSON.parse(localStorage.getItem("cc-ad-generation-defaults")) || {};

/* Core fields (v1, kept for backwards compatibility) */
let dialogueSource = $state(adDefaults.dialogueSource || "transcription"),
    selectedEventGroupIndex = $state(null),
    transcriptionProvider = $state(adDefaults.transcriptionProvider || "Speechmatics"),
    language = $state(adDefaults.language || "en"),
    llmProvider = $state(adDefaults.llmProvider || "Anthropic"),
    minEventDuration = $state(adDefaults.minEventDuration || 2),
    maxEventDuration = $state(adDefaults.maxEventDuration || 12),
    contentDescription = $state(adDefaults.contentDescription || ""),
    mainCharacters = $state(adDefaults.mainCharacters || ""),
    additionalInstructions = $state(adDefaults.additionalInstructions || ""),
    waiting = $state(false),
    statusMsg = $state(""),
    progress = $state(0);


/* v2 fields — fall back to safe defaults so older saved presets still load */
let backupLlmProvider = $state(adDefaults.backupLlmProvider ?? ""),
    outputLanguage = $state(adDefaults.outputLanguage ?? "en"),
    descriptionLength = $state(adDefaults.descriptionLength ?? "standard"),
    descriptionFocus = $state(Array.isArray(adDefaults.descriptionFocus) && adDefaults.descriptionFocus.length > 0
        ? adDefaults.descriptionFocus
        : [...DEFAULT_FOCUS]),
    avoidDuplicates = $state(adDefaults.avoidDuplicates ?? true),
    commercialAd = $state(adDefaults.commercialAd ?? false),
    bypassDialogue = $state(adDefaults.bypassDialogue ?? false),
    maxWpm = $state(adDefaults.maxWpm ?? adDefaults.targetWpm ?? 150);

/* v3 segment-selection fields — SMPTE strings so the UI round-trips visually.
   `segmentEndTc` starts empty and gets filled in once we know the video duration.
   `processEntireVideo` is the explicit opt-out: when true (the default) the segment
   inputs are hidden and the cloud function processes the whole clip; when false the
   user can trim to a sub-range via the timecode inputs. */
let processEntireVideo = $state(adDefaults.processEntireVideo ?? true),
    segmentStartTc = $state(adDefaults.segmentStartTc ?? "00:00:00:00"),
    segmentEndTc = $state(adDefaults.segmentEndTc ?? "");

/* Section collapse state */
let openSegment = $state(true),
    openDialogue = $state(true),
    openAiModels = $state(false),
    openOutput = $state(false),
    openDisclaimers = $state(false),
    openTiming = $state(false),
    openContext = $state(false);

let userId = firebase.auth().currentUser.uid,
    userEmail = firebase.auth().currentUser.email,
    teamId = authState.team ? authState.team.id : null,
    teamOwner = authState.team ? authState.team.owner : null,
    teamOwnerId = authState.team ? authState.team.ownerId : null,
    homeRef = authState.team ? db.collection("teams").doc(teamId).collection("jobs") : db.collection("users").doc(userId).collection("jobs"),
    storageRef = storage.ref(),
    uploadBasePath = authState.team ? "teams/" + teamId + "/jobs/ad/" : "users/" + userId + "/jobs/ad/";

/* Computed */
let videoDurationSeconds = $state(0);
let estimatedCost = $derived(Math.max(3, Math.ceil(videoDurationSeconds / 60)) );
let durationMinutes = $derived(Math.ceil(videoDurationSeconds / 60));
let availableEventGroups = $derived(
    eventGroupState
        .map((evg, index) => ({ ...evg, _index: index }))
        .filter(evg => (evg.type === "subtitle" || evg.type === "transcription") && evg.events.length > 0)
);
let isTrial = $derived(authState.status === "in_trial");
let trialTooLong = $derived(isTrial && videoDurationSeconds > TRIAL_MAX_DURATION_SECONDS);

/* Parse the SMPTE inputs to seconds. `tcLib.tcToSec` throws on malformed input,
   so guard with a try/catch and surface that as "invalid" rather than crashing. */
function parseTcSafe(tc) {
    if (typeof tc !== "string" || tc.trim() === "") return NaN;
    try {
        return tcLib.tcToSec(tc, projectState.frameRate, projectState.dropFrame);
    } catch (_e) {
        return NaN;
    }
}

let segmentStartSec = $derived(parseTcSafe(segmentStartTc));
let segmentEndSec = $derived(parseTcSafe(segmentEndTc));
/* Segment is invalid if either parse fails, end <= start, or end exceeds the
   video. While the video duration is still loading we leave the state valid
   so the user isn't told they're broken before we know the answer. When the
   user has opted into "process entire video" we skip validation entirely
   because the timecode fields are not used. */
let segmentInvalid = $derived(
    !processEntireVideo
        && videoDurationSeconds > 0
        && (
            !Number.isFinite(segmentStartSec)
            || !Number.isFinite(segmentEndSec)
            || segmentStartSec < 0
            || segmentEndSec <= segmentStartSec
            || segmentEndSec > videoDurationSeconds + 0.001
        )
);

/* Get video duration on mount */
onMount(() => {
    getVideoDuration();
});

async function getVideoDuration() {
    try {
        if (playerState.duration) {
            videoDurationSeconds = playerState.duration;
        } else {
            var matches;
            try {
                let ffmpegRes = await exec(
                    `${window.ffmpegPath.replace("app.asar", "app.asar.unpacked")} -y -i "${projectState.media.localPath}"`,
                );
                matches = ffmpegRes.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            } catch (err) {
                matches = err.message.match(
                    /(?<=duration\:\s)\d\d:\d\d:\d\d\.\d\d/im,
                );
            }

            if (matches) {
                videoDurationSeconds = tcLib.tcMsToSec(matches[0]);
            }
        }

        // If the user hasn't typed an end timecode yet, default it to the full
        // video duration so the segment defaults to the whole clip (which makes
        // the feature behave exactly like the old pipeline for users who never
        // open the segment section).
        if ((!segmentEndTc || segmentEndTc.trim() === "") && videoDurationSeconds > 0) {
            segmentEndTc = tcLib.secToTc(videoDurationSeconds, projectState.frameRate, projectState.dropFrame);
        }
    } catch (err) {
        console.error("Error getting video duration:", err);
    }
}

function getLanguageMap() {
    if (transcriptionProvider === "Deepgram") {
        return deepgramLanguages;
    } else if (transcriptionProvider === "Assembly AI") {
        return assemblyAiLanguages;
    } else {
        return speechmaticsLanguages;
    }
}

function resetLanguage() {
    language = "en";
}

function onDialogueSourceChange() {
    if (dialogueSource === "transcription") {
        selectedEventGroupIndex = null;
    } else {
        selectedEventGroupIndex = parseInt(dialogueSource);
    }
}

function onBypassDialogueChange() {
    if (bypassDialogue) {
        dialogueSource = "transcription";
        selectedEventGroupIndex = null;
    }
}

function buildEmptyDialogueEventGroup() {
    return {
        name: "Dialogue",
        type: "subtitle",
        events: [],
        ad: false,
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame
    };
}

function toggleFocus(id) {
    if (descriptionFocus.includes(id)) {
        descriptionFocus = descriptionFocus.filter(f => f !== id);
    } else {
        descriptionFocus = [...descriptionFocus, id];
    }
}

async function submitAdJob() {
    try {
        if (trialTooLong) {
            toast.push(m.ad_trial_too_long(), { classes: ["toast-warning"] });
            return;
        }

        if (segmentInvalid) {
            openSegment = true;
            toast.push(m.ad_segment_invalid(), { classes: ["toast-warning"] });
            return;
        }

        // Resolve segment selection. When "process entire video" is on we force
        // the whole-video range so the cloud function and ffmpeg helpers see the
        // same defaults the cloud config does (start=0, end=duration). Otherwise
        // `clampSegment` snaps to [0, duration] and guarantees `end >= start`,
        // so the ffmpeg / Firestore values stay sane even if the user typed
        // garbage that slipped past `segmentInvalid`.
        const seg = processEntireVideo
            ? clampSegment(0, videoDurationSeconds, videoDurationSeconds)
            : clampSegment(segmentStartSec, segmentEndSec, videoDurationSeconds);

        // Confirm cost
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.confirm_ad_job_title(),
            text: m.confirm_ad_job_text({ cost: estimatedCost.toFixed(2) }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: m.yes(),
            cancelButtonText: m.cancel()
        });

        if (!swalRes.isConfirmed) {
            return;
        }

        const useBypassDialogue = bypassDialogue;
        const useExistingDialogue = !useBypassDialogue && selectedEventGroupIndex !== null;

        waiting = true;
        progress = 0;
        statusMsg = bypassDialogue ? m.detecting_scenes() : m.extracting_audio();

        // Save user preferences. We persist the raw SMPTE strings (not seconds)
        // so the dropdown round-trips visually for the user next time, and so
        // it stays correct across different project frame rates.
        localStorage.setItem("cc-ad-generation-defaults", JSON.stringify({
            dialogueSource: useBypassDialogue ? "bypass" : (selectedEventGroupIndex !== null ? "eventGroup" : "transcription"),
            transcriptionProvider,
            language,
            llmProvider,
            minEventDuration,
            maxEventDuration,
            contentDescription,
            mainCharacters,
            additionalInstructions,
            backupLlmProvider,
            outputLanguage,
            descriptionLength,
            descriptionFocus,
            avoidDuplicates,
            commercialAd,
            bypassDialogue,
            maxWpm,
            processEntireVideo,
            segmentStartTc,
            segmentEndTc
        }));

        const jobId = uuidv4();
        const jobStoragePath = uploadBasePath + jobId + "/";
        const tempDir = os.tmpdir() + path.sep + "cc-ad-" + jobId;

        // Create temp directory
        if (!fs.existsSync(tempDir)) {
            fs.mkdirSync(tempDir, { recursive: true });
        }
        const framesDir = tempDir + path.sep + "frames";
        if (!fs.existsSync(framesDir)) {
            fs.mkdirSync(framesDir, { recursive: true });
        }

        // Step 1: Register job document in Firestore
        const jobDoc = {
            id: jobId,
            type: "automatic ad",
            status: "Submitted",
            progress: 0,
            statusMsg: "Extracting resources...",
            currentStep: "extraction",
            failedStep: null,
            failedError: null,
            projectId: projectState.id,
            projectName: projectState.name,
            submittedBy: userEmail,
            userId: userId,
            teamId: teamId,
            owner: teamOwner,
            ownerId: teamOwnerId,
            duration: videoDurationSeconds,
            cost: 0,
            chars: 0,
            deleted: false,
            archived: false,
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
            completedOn: null,
            config: {
                configSchemaVersion: 2,
                dialogueSource: useBypassDialogue ? "bypass" : (useExistingDialogue ? "eventGroup" : "transcription"),
                bypassDialogue: useBypassDialogue,
                transcriptionProvider: transcriptionProvider,
                language: language,
                llmProvider: llmProvider,
                backupLlmProvider: backupLlmProvider || null,
                outputLanguage: outputLanguage,
                descriptionLength: descriptionLength,
                descriptionFocus: descriptionFocus,
                avoidDuplicates: avoidDuplicates,
                commercialAd: commercialAd,
                minEventDuration: minEventDuration,
                maxEventDuration: maxEventDuration,
                maxWpm: maxWpm,
                segmentStart: seg.start,
                segmentEnd: seg.end,
                frameRate: projectState.frameRate,
                dropFrame: projectState.dropFrame,
                userContext: {
                    contentDescription: contentDescription,
                    mainCharacters: mainCharacters,
                    additionalInstructions: additionalInstructions
                }
            }
        };

        await homeRef.doc(jobId).set(jobDoc);
        progress = 5;

        if (useBypassDialogue) {
            statusMsg = m.uploading_dialogue();
            await storageRef.child(jobStoragePath + "eventGroups/dialogue.json").putString(JSON.stringify(buildEmptyDialogueEventGroup()));
            progress = 15;
        } else if (useExistingDialogue) {
            // Upload the selected Event Group as dialogue.json
            statusMsg = m.uploading_dialogue();
            const selectedEvg = JSON.parse(JSON.stringify(eventGroupState[selectedEventGroupIndex]));
            selectedEvg.name = "Dialogue";
            await storageRef.child(jobStoragePath + "eventGroups/dialogue.json").putString(JSON.stringify(selectedEvg));
            progress = 15;
        } else {
            // Step 2: Extract audio (16kHz mono WAV) — restricted to the selected
            // segment so the cloud transcriber only processes (and bills against)
            // the audio the user actually wants described.
            statusMsg = m.extracting_audio();
            const audioOutputPath = tempDir + path.sep + "audio.wav";
            await extractAudio(projectState.media.localPath, audioOutputPath, seg.start, seg.duration);
            progress = 20;
        }

        // Step 3: Detect scene changes. When the user pulled in markers from the
        // project, shift them into segment-relative time and drop any that fall
        // outside the segment so the shot list lines up with the audio / frames.
        statusMsg = m.detecting_scenes();
        const shotsOutputPath = tempDir + path.sep + "shots.json";
        let shots;

        const existingMarkers = markerState.lists[0]?.markers || [];
        if (existingMarkers.length > 0) {
            const shotSourceRes = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.ad_shot_source_title(),
                text: m.ad_shot_source_text({ count: existingMarkers.length }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: m.ad_shot_source_use_markers(),
                cancelButtonText: m.ad_shot_source_detect_auto()
            });

            if (shotSourceRes.isConfirmed) {
                shots = existingMarkers
                    .map(marker => ({ time: marker.time, score: 1 }))
                    .filter(s => s.time >= seg.start && s.time <= seg.end)
                    .map(s => ({ time: s.time - seg.start, score: s.score }))
                    .sort((a, b) => a.time - b.time);
            } else {
                shots = await detectSceneChanges({
                    inputPath: projectState.media.localPath,
                    startSec: seg.start,
                    durationSec: seg.duration,
                    sensitivity: "ai",
                });
            }
        } else {
            shots = await detectSceneChanges({
                inputPath: projectState.media.localPath,
                startSec: seg.start,
                durationSec: seg.duration,
                sensitivity: "ai",
            });
        }

        fs.writeFileSync(shotsOutputPath, JSON.stringify(shots));
        progress = 35;

        // Step 4: Extract keyframes (1fps with near-duplicate removal)
        statusMsg = m.extracting_frames();
        await extractFrames(projectState.media.localPath, framesDir, seg.start, seg.duration);
        progress = 50;

        statusMsg = m.ad_extraction_complete();

        // Step 5: Upload audio (only needed for transcription path)
        if (!useBypassDialogue && !useExistingDialogue) {
            statusMsg = m.uploading_audio();
            const audioOutputPath = tempDir + path.sep + "audio.wav";
            const audioBuffer = fs.readFileSync(audioOutputPath);
            await storageRef.child(jobStoragePath + "audio/audio.wav").put(audioBuffer);
        }
        progress = 60;

        // Step 6: Upload shots.json
        statusMsg = m.uploading_shots();
        await storageRef.child(jobStoragePath + "shots/shots.json").putString(JSON.stringify(shots));
        progress = 65;

        // Step 7: Upload frames
        const frameFiles = fs.readdirSync(framesDir).filter(f => f.endsWith('.jpeg')).sort();
        const totalFrames = frameFiles.length;
        let uploadedFrames = 0;

        for (const frameFile of frameFiles) {
            statusMsg = m.uploading_frames({ completed: uploadedFrames, total: totalFrames });
            const framePath = framesDir + path.sep + frameFile;
            const frameBuffer = fs.readFileSync(framePath);
            await storageRef.child(jobStoragePath + "frames/" + frameFile).put(frameBuffer);
            uploadedFrames++;
            progress = 65 + Math.floor((uploadedFrames / totalFrames) * 25);
        }

        progress = 90;
        statusMsg = m.submitting_ad_job();

        // Step 8: Update job status and call cloud function
        await homeRef.doc(jobId).update({
            status: "Processing",
            statusMsg: "Queued for processing",
            currentStep: "queued",
            progress: 20,
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
        });

        await firebase.functions().httpsCallable("v8SubmitAdGenerationJob")({ jobId, teamId });

        progress = 100;
        statusMsg = m.ad_upload_complete();

        toast.push(m.ad_job_submitted_success(), {
            classes: ["toast-success"]
        });

        // Clean up temp files
        try {
            fs.rmSync(tempDir, { recursive: true, force: true });
        } catch (cleanupErr) {
            console.warn("Failed to clean up temp directory:", cleanupErr);
        }

        showModal("aiAdImport");
    } catch (err) {
        console.error(err);
        waiting = false;
        toast.push(m.ad_job_failed(), { classes: ["toast-danger"] });
    }
}

/* All four ffmpeg helpers below accept optional `startSec` / `durationSec` so
   we can trim to the segment via `-ss` / `-t`. Passing `undefined` for both
   keeps the original whole-video behaviour for any unrelated callers. */
function extractAudio(inputPath, outputPath, startSec, durationSec) {
    return new Promise((resolve, reject) => {
        const cmd = ffmpeg(inputPath);
        if (startSec != null && startSec > 0) cmd.seekInput(startSec);
        if (durationSec != null && durationSec > 0) cmd.duration(durationSec);
        cmd.audioCodec('pcm_s16le')
            .audioChannels(1)
            .audioFrequency(16000)
            .output(outputPath)
            .on('end', resolve)
            .on('error', reject)
            .run();
    });
}

function extractFramesAtFps(inputPath, outputDir, startSec, durationSec) {
    return new Promise((resolve, reject) => {
        const cmd = ffmpeg(inputPath);
        if (startSec != null && startSec > 0) cmd.seekInput(startSec);
        if (durationSec != null && durationSec > 0) cmd.duration(durationSec);
        cmd.videoFilters('fps=1')
            .outputOptions('-q:v 5')
            .output(outputDir + path.sep + '%05d.jpg')
            .on('end', resolve)
            .on('error', reject)
            .run();
    });
}

function generateFingerprints(inputPath, outputPath, startSec, durationSec) {
    return new Promise((resolve, reject) => {
        const cmd = ffmpeg(inputPath);
        if (startSec != null && startSec > 0) cmd.seekInput(startSec);
        if (durationSec != null && durationSec > 0) cmd.duration(durationSec);
        cmd.videoFilters('fps=1,scale=16:16')
            .noAudio()
            .outputOptions(['-f rawvideo', '-pix_fmt gray'])
            .output(outputPath)
            .on('end', resolve)
            .on('error', reject)
            .run();
    });
}

/**
 * Compare 16x16 grayscale fingerprints of sequential frames.
 * Frames whose Mean Absolute Difference vs. the last kept frame
 * is below the threshold are deleted as near-duplicates.
 * Remaining frames are renamed to timestamp format (e.g. "5s.jpeg").
 */
function deduplicateFrames(framesDir, fingerprintPath) {
    const SIMILARITY_THRESHOLD = 5;
    const fingerprints = fs.readFileSync(fingerprintPath);
    const fpSize = 16 * 16;
    const frameCount = Math.floor(fingerprints.length / fpSize);
    const allFrames = fs.readdirSync(framesDir)
        .filter(f => f.endsWith('.jpg'))
        .sort();

    const count = Math.min(frameCount, allFrames.length);
    const framesToDelete = new Set();
    let lastKeptIndex = 0;

    for (let i = 1; i < count; i++) {
        const lastKeptFp = fingerprints.slice(lastKeptIndex * fpSize, (lastKeptIndex + 1) * fpSize);
        const currFp = fingerprints.slice(i * fpSize, (i + 1) * fpSize);

        let totalDiff = 0;
        for (let j = 0; j < fpSize; j++) {
            totalDiff += Math.abs(lastKeptFp[j] - currFp[j]);
        }
        const mad = totalDiff / fpSize;

        if (mad >= SIMILARITY_THRESHOLD) {
            lastKeptIndex = i;
        } else {
            framesToDelete.add(i);
        }
    }

    for (let i = 0; i < count; i++) {
        const framePath = framesDir + path.sep + allFrames[i];
        if (framesToDelete.has(i)) {
            fs.unlinkSync(framePath);
        } else {
            fs.renameSync(framePath, framesDir + path.sep + i + 's.jpeg');
        }
    }

    return { kept: count - framesToDelete.size, total: count };
}

async function extractFrames(inputPath, outputDir, startSec, durationSec) {
    const fingerprintPath = path.dirname(outputDir) + path.sep + 'fingerprints.raw';
    await extractFramesAtFps(inputPath, outputDir, startSec, durationSec);
    await generateFingerprints(inputPath, fingerprintPath, startSec, durationSec);
    const result = deduplicateFrames(outputDir, fingerprintPath);
    try { fs.unlinkSync(fingerprintPath); } catch (e) { /* ignore */ }
    console.log(`Frame extraction: ${result.kept} unique keyframes kept out of ${result.total} (${result.total - result.kept} duplicates removed)`);
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'automaticAd' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="AutomaticAdModal"
    aria-labelledby="automaticAdTitle">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="automaticAdTitle">{m.automatic_ad_job()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p class="text-muted small">{m.automatic_ad_description()}</p>

                {#if isTrial}
                    <div class="alert alert-info small mb-3" role="status">
                        <i class="bi bi-info-circle" aria-hidden="true"></i>
                        {m.ad_trial_banner()}
                    </div>
                {/if}

                <form onsubmit={preventDefault(submitAdJob)}>
                    <!-- 1. Segment to Process -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openSegment = !openSegment}
                             onkeydown={(e) => e.key === 'Enter' && (openSegment = !openSegment)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-scissors"></i> {m.ad_section_segment()}</h6>
                                <i class="bi bi-chevron-{openSegment ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openSegment}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="form-check form-switch mb-2">
                                    <input class="form-check-input" type="checkbox" role="switch"
                                           id="adProcessEntireVideo"
                                           bind:checked={processEntireVideo}
                                           disabled={waiting} />
                                    <label class="form-check-label" for="adProcessEntireVideo">
                                        {m.ad_segment_process_entire_video()}
                                    </label>
                                </div>
                                <p class="small text-muted">
                                    {processEntireVideo
                                        ? m.ad_segment_help_entire_video()
                                        : m.ad_segment_help()}
                                </p>
                                {#if !processEntireVideo}
                                    <div class="row">
                                        <div class="col-6">
                                            <label class="form-label" for="adSegmentStart">{m.ad_segment_start_label()}</label>
                                            <input class="form-control" id="adSegmentStart" type="text"
                                                   bind:value={segmentStartTc}
                                                   placeholder="00:00:00:00"
                                                   disabled={waiting} />
                                        </div>
                                        <div class="col-6">
                                            <label class="form-label" for="adSegmentEnd">{m.ad_segment_end_label()}</label>
                                            <input class="form-control" id="adSegmentEnd" type="text"
                                                   bind:value={segmentEndTc}
                                                   placeholder="00:00:00:00"
                                                   disabled={waiting} />
                                        </div>
                                    </div>
                                    {#if segmentInvalid}
                                        <div class="alert alert-warning small mt-3 mb-0" role="alert">
                                            <i class="bi bi-exclamation-triangle"></i> {m.ad_segment_invalid()}
                                        </div>
                                    {/if}
                                {/if}
                            </div>
                        {/if}
                    </div>

                    <!-- 2. Dialogue & Source -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openDialogue = !openDialogue}
                             onkeydown={(e) => e.key === 'Enter' && (openDialogue = !openDialogue)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-mic"></i> {m.ad_section_dialogue_source()}</h6>
                                <i class="bi bi-chevron-{openDialogue ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openDialogue}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="form-check form-switch mb-3">
                                    <input class="form-check-input" type="checkbox" id="adBypassDialogue"
                                           bind:checked={bypassDialogue}
                                           onchange={onBypassDialogueChange}
                                           disabled={waiting} />
                                    <label class="form-check-label" for="adBypassDialogue">
                                        {m.ad_bypass_dialogue_label()}
                                    </label>
                                    <div class="form-text text-muted">{m.ad_bypass_dialogue_help()}</div>
                                </div>

                                {#if !bypassDialogue}
                                <div class="mb-3">
                                    <label class="form-label" for="adDialogueSource">{m.dialogue_source()}</label>
                                    <select class="form-select" id="adDialogueSource"
                                            bind:value={dialogueSource}
                                            onchange={onDialogueSourceChange}
                                            disabled={waiting}>
                                        <option value="transcription">{m.automatic_transcription()}</option>
                                        {#each availableEventGroups as evg (evg.id)}
                                            <option value={evg._index}>{m.event_group_option_label({ name: evg.name, count: evg.events.length })}</option>
                                        {/each}
                                    </select>
                                    <small class="form-text text-muted">{m.dialogue_source_help()}</small>
                                </div>

                                {#if dialogueSource === "transcription"}
                                    <div class="mb-3">
                                        <label class="form-label" for="adTranscriptionProvider">{m.transcription_provider()}</label>
                                        <select class="form-select" id="adTranscriptionProvider"
                                                bind:value={transcriptionProvider}
                                                onchange={resetLanguage}
                                                disabled={waiting}>
                                            <option>Assembly AI</option>
                                            <option>Deepgram</option>
                                            <option>Speechmatics</option>
                                        </select>
                                    </div>

                                    <div class="mb-0">
                                        <label class="form-label" for="adSourceLanguage">{m.source_language()}</label>
                                        <select class="form-select" id="adSourceLanguage" bind:value={language} disabled={waiting}>
                                            {#each Object.entries(getLanguageMap()) as [code, name] (code)}
                                                <option value={code}>{name}</option>
                                            {/each}
                                        </select>
                                    </div>
                                {/if}
                                {/if}
                            </div>
                        {/if}
                    </div>

                    <!-- 2. AI Models -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openAiModels = !openAiModels}
                             onkeydown={(e) => e.key === 'Enter' && (openAiModels = !openAiModels)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-cpu"></i> {m.ad_section_ai_models()}</h6>
                                <i class="bi bi-chevron-{openAiModels ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openAiModels}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="mb-3">
                                    <label class="form-label" for="adLlmProvider">{m.llm_provider()}</label>
                                    <select class="form-select" id="adLlmProvider" bind:value={llmProvider} disabled={waiting}>
                                        <option>Anthropic</option>
                                        <option>Gemini</option>
                                        <option>OpenAI</option>
                                    </select>
                                    <small class="form-text text-muted">{m.llm_provider_help()}</small>
                                </div>

                                <div class="mb-0">
                                    <label class="form-label" for="adBackupLlmProvider">{m.ad_backup_llm_label()}</label>
                                    <select class="form-select" id="adBackupLlmProvider" bind:value={backupLlmProvider} disabled={waiting}>
                                        <option value="">{m.ad_backup_llm_none()}</option>
                                        <option value="Anthropic">Anthropic</option>
                                        <option value="Gemini">Gemini</option>
                                        <option value="OpenAI">OpenAI</option>
                                    </select>
                                    <small class="form-text text-muted">{m.ad_backup_llm_help()}</small>
                                </div>
                            </div>
                        {/if}
                    </div>

                    <!-- 3. Output & Style -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openOutput = !openOutput}
                             onkeydown={(e) => e.key === 'Enter' && (openOutput = !openOutput)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-pencil-square"></i> {m.ad_section_output_style()}</h6>
                                <i class="bi bi-chevron-{openOutput ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openOutput}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="mb-3">
                                    <label class="form-label" for="adOutputLanguage">{m.ad_output_language_label()}</label>
                                    <select class="form-select" id="adOutputLanguage" bind:value={outputLanguage} disabled={waiting}>
                                        {#each OUTPUT_LANGUAGES as opt (opt.code)}
                                            <option value={opt.code}>{opt.label}</option>
                                        {/each}
                                    </select>
                                    <small class="form-text text-muted">{m.ad_output_language_help()}</small>
                                </div>

                                <div class="mb-3">
                                    <label class="form-label" for="adDescriptionLength">{m.ad_description_length_label()}</label>
                                    <select class="form-select" id="adDescriptionLength" bind:value={descriptionLength} disabled={waiting}>
                                        {#each Object.entries(DESCRIPTION_LENGTH_PRESETS) as [key, preset] (key)}
                                            <option value={key}>{preset.label}</option>
                                        {/each}
                                    </select>
                                    <small class="form-text text-muted">{m.ad_description_length_help()}</small>
                                </div>

                                <div class="form-check form-switch mb-3">
                                    <input class="form-check-input" type="checkbox" id="adAvoidDuplicates"
                                           bind:checked={avoidDuplicates} disabled={waiting} />
                                    <label class="form-check-label" for="adAvoidDuplicates">
                                        {m.ad_avoid_duplicates_label()}
                                    </label>
                                    <div class="form-text text-muted">{m.ad_avoid_duplicates_help()}</div>
                                </div>

                                <fieldset class="mb-0">
                                    <legend class="form-label fs-6">{m.ad_description_focus_label()}</legend>
                                    <div class="row g-2">
                                        {#each DESCRIPTION_FOCUS_OPTIONS as opt (opt.id)}
                                            <div class="col-12 col-sm-6">
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox"
                                                           id="adFocus-{opt.id}"
                                                           checked={descriptionFocus.includes(opt.id)}
                                                           onchange={() => toggleFocus(opt.id)}
                                                           disabled={waiting} />
                                                    <label class="form-check-label" for="adFocus-{opt.id}">{opt.label}</label>
                                                </div>
                                            </div>
                                        {/each}
                                    </div>
                                    <small class="form-text text-muted">{m.ad_description_focus_help()}</small>
                                </fieldset>
                            </div>
                        {/if}
                    </div>

                    <!-- 5. Disclaimers (commercial AD) -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openDisclaimers = !openDisclaimers}
                             onkeydown={(e) => e.key === 'Enter' && (openDisclaimers = !openDisclaimers)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-megaphone"></i> {m.ad_section_disclaimers()}</h6>
                                <i class="bi bi-chevron-{openDisclaimers ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openDisclaimers}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="form-check form-switch mb-3">
                                    <input class="form-check-input" type="checkbox" id="adCommercialAd"
                                           bind:checked={commercialAd} disabled={waiting} />
                                    <label class="form-check-label" for="adCommercialAd">
                                        {m.ad_commercial_ad_label()}
                                    </label>
                                    <div class="form-text text-muted">{m.ad_commercial_ad_help()}</div>
                                </div>

                            </div>
                        {/if}
                    </div>

                    <!-- 5. Script Timing -->
                    <div class="card mb-2">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openTiming = !openTiming}
                             onkeydown={(e) => e.key === 'Enter' && (openTiming = !openTiming)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-stopwatch"></i> {m.ad_section_script_timing()}</h6>
                                <i class="bi bi-chevron-{openTiming ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openTiming}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="row">
                                    <div class="col-6">
                                        <label class="form-label" for="adMinDuration">{m.min_event_duration()}</label>
                                        <input type="number" class="form-control" id="adMinDuration" bind:value={minEventDuration}
                                               min="1" max="10" step="0.5" disabled={waiting} />
                                    </div>
                                    <div class="col-6">
                                        <label class="form-label" for="adMaxDuration">{m.max_event_duration()}</label>
                                        <input type="number" class="form-control" id="adMaxDuration" bind:value={maxEventDuration}
                                               min="3" max="30" step="0.5" disabled={waiting} />
                                    </div>
                                </div>
                                <div class="mt-3 mb-0">
                                    <label class="form-label" for="adMaxWpm">{m.ad_max_wpm_label()}</label>
                                    <select class="form-select" id="adMaxWpm" bind:value={maxWpm} disabled={waiting}>
                                        {#each MAX_WPM_PRESETS as p (p.wpm)}
                                            <option value={p.wpm}>{p.label}</option>
                                        {/each}
                                    </select>
                                    <small class="form-text text-muted">{m.ad_max_wpm_help()}</small>
                                </div>
                            </div>
                        {/if}
                    </div>

                    <!-- 6. Project Context -->
                    <div class="card mb-3">
                        <div class="card-header bg-light" role="button" tabindex="0"
                             onclick={() => openContext = !openContext}
                             onkeydown={(e) => e.key === 'Enter' && (openContext = !openContext)}>
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><i class="bi bi-info-square"></i> {m.ad_section_project_context()} <span class="badge bg-secondary">{m.ad_section_optional()}</span></h6>
                                <i class="bi bi-chevron-{openContext ? 'up' : 'down'}"></i>
                            </div>
                        </div>
                        {#if openContext}
                            <div class="card-body" transition:fade={{ duration: 150 }}>
                                <div class="mb-3">
                                    <label class="form-label" for="adContentDesc">{m.content_description_label()}</label>
                                    <textarea class="form-control" id="adContentDesc" rows="2"
                                              bind:value={contentDescription}
                                              placeholder={m.content_description_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                                <div class="mb-3">
                                    <label class="form-label" for="adCharacters">{m.main_characters_label()}</label>
                                    <textarea class="form-control" id="adCharacters" rows="2"
                                              bind:value={mainCharacters}
                                              placeholder={m.main_characters_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                                <div class="mb-0">
                                    <label class="form-label" for="adInstructions">{m.additional_instructions_label()}</label>
                                    <textarea class="form-control" id="adInstructions" rows="2"
                                              bind:value={additionalInstructions}
                                              placeholder={m.additional_instructions_placeholder()}
                                              disabled={waiting}></textarea>
                                </div>
                            </div>
                        {/if}
                    </div>

                    <!-- Sticky footer area: trial gate, cost, submit -->
                    {#if trialTooLong}
                        <div class="alert alert-warning small" role="alert">
                            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
                            {m.ad_trial_too_long()}
                        </div>
                    {/if}

                    <h6 class="text-warning">{m.cost_estimate()}</h6>
                    <div class="card mb-3">
                        <div class="card-body py-2">
                            <p class="mb-1"><strong>{m.video_duration()}:</strong> {durationMinutes > 0 ? m.duration_minutes({ minutes: durationMinutes }) : '...'}</p>
                            <p class="mb-1"><strong>{m.cost_per_minute()}</strong></p>
                            <p class="mb-1 text-muted small">{m.minimum_charge()}</p>
                            <p class="mb-0"><strong>{m.estimated_total({ cost: estimatedCost.toFixed(2) })}</strong></p>
                        </div>
                    </div>
                </form>

                <button class="btn btn-primary float-end" type="button"
                        disabled={waiting || videoDurationSeconds === 0 || trialTooLong || segmentInvalid}
                        onclick={submitAdJob}>
                    {m.submit_ad_job()}
                </button>

                {#if waiting}
                <div class="mt-3" role="status" aria-live="polite">
                    <BarLoader size="150" color="#1eb4b2" unit="px" duration="3s" />
                    <p class="small text-muted">
                        {statusMsg} ({progress}%)
                    </p>
                </div>
                {/if}
            </div>
        </div>
    </div>
</div>
